import type { ConsentGate } from '@toothsomatics/consent-catalog';

/**
 * The one place the portal talks to the API.
 *
 * The base URL comes from the build: `https://api.toothsomatics.com` in production, localhost in
 * development. Nothing here decides anything — every rule the responses imply is enforced by the
 * server, and this client only carries the request and reads the reply.
 *
 * A non-2xx becomes an `ApiError` carrying the server's code, so a screen can tell a consent
 * refusal (409) apart from a genuine fault and respond in kind rather than showing one blanket
 * message for everything.
 */
const BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The signed-in client's id token, read fresh per request so a sign-out is felt immediately. */
function authHeader(): Record<string, string> {
  const raw = localStorage.getItem('ts.session');
  if (!raw) return {};
  try {
    const session = JSON.parse(raw) as { idToken?: string };
    return session.idToken ? { Authorization: `Bearer ${session.idToken}` } : {};
  } catch {
    return {};
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...init?.headers },
  });

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const error = body as { error?: string; message?: string; details?: unknown };
    throw new ApiError(
      response.status,
      error.error ?? 'INTERNAL',
      error.message ?? 'Something went wrong.',
      error.details,
    );
  }

  return body as T;
}

export interface CheckoutContext {
  readonly plan: {
    readonly slug: string;
    readonly name: string;
    readonly summary: string;
    readonly amountMinor: number;
    readonly currency: string;
    readonly turnaroundDays: number;
  };
  readonly consents: ReadonlyArray<{
    readonly gate: ConsentGate;
    readonly documentId: string;
    readonly version: number;
    readonly sha256: string;
    readonly title: string;
    readonly acknowledgement: string;
    readonly bodyMd: string;
  }>;
}

export function getCheckoutContext(planSlug: string): Promise<CheckoutContext> {
  return request<CheckoutContext>(`/v1/checkout/context?plan=${encodeURIComponent(planSlug)}`);
}

export interface CheckoutIntentBody {
  readonly planSlug: string;
  readonly email: string;
  readonly consents: ReadonlyArray<{
    readonly gate: ConsentGate;
    readonly documentId: string;
    readonly version: number;
    readonly sha256: string;
    readonly accepted: true;
    readonly context: { readonly locale: string; readonly scrolledToEnd: boolean };
  }>;
}

export interface CheckoutIntentResult {
  readonly purchaseId: string;
  readonly checkoutUrl: string;
  readonly expiresAt: string;
}

export function startCheckout(body: CheckoutIntentBody): Promise<CheckoutIntentResult> {
  return request<CheckoutIntentResult>('/v1/checkout/intent', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface PurchaseStatus {
  readonly purchaseId: string;
  readonly status: 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled';
  readonly accountInviteSent: boolean;
}

export function getPurchaseStatus(purchaseId: string): Promise<PurchaseStatus> {
  return request<PurchaseStatus>(`/v1/purchases/${encodeURIComponent(purchaseId)}/status`);
}

/** Price in the plan's own currency, formatted for display. Amounts are minor units. */
export function formatPrice(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amountMinor / 100);
}

// ── The reflective exercise ───────────────────────────────────────────────────

export interface DraftView {
  readonly submissionId: string;
  readonly status: string;
  readonly chart: ReadonlyArray<{ toothNumber: number; selections: unknown; note: string | null }>;
  readonly answers: ReadonlyArray<{ questionKey: string; value: string | null }>;
  readonly consents: ReadonlyArray<{
    readonly gate: string;
    readonly documentId: string;
    readonly version: number;
    readonly sha256: string;
    readonly acknowledgement: string;
    readonly bodyMd: string;
  }>;
}

/** Open (or create) the signed-in client's draft, with whatever is already saved. */
export function openDraft(): Promise<DraftView> {
  return request<DraftView>('/v1/submissions/open', { method: 'POST' });
}

export interface SaveDraftBody {
  readonly chart: ReadonlyArray<{
    readonly toothNumber: number;
    readonly selections: Record<string, string[]>;
    readonly note?: string;
  }>;
  readonly answers: ReadonlyArray<{ readonly questionKey: string; readonly value: string }>;
}

export function saveDraft(submissionId: string, body: SaveDraftBody): Promise<{ saved: true }> {
  return request(`/v1/submissions/${encodeURIComponent(submissionId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export interface SubmitBody {
  readonly consents: ReadonlyArray<{
    readonly gate: string;
    readonly version: number;
    readonly sha256: string;
    readonly accepted: boolean;
  }>;
}

export function submitDraft(
  submissionId: string,
  body: SubmitBody,
): Promise<{ status: string; dueBy: string }> {
  return request(`/v1/submissions/${encodeURIComponent(submissionId)}/submit`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
